import { useTranslator } from '@translator-app/react';
import Fuse from 'fuse.js';
import { GetServerSidePropsContext } from 'next';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';

import { Button, LinkButton } from '../../../components/atoms/button';
import { Heading } from '../../../components/atoms/heading';
import { Image } from '../../../components/atoms/image';
import { TextField } from '../../../components/atoms/textfield';
import { AppBanner } from '../../../components/organisms/app-banner';
import { ErrorFallback } from '../../../components/organisms/error-boundary';
import { NotFound } from '../../../components/organisms/not-found';
import { OrderHint } from '../../../components/organisms/order-hint';
import { FallbackPageTemplate } from '../../../components/templates/fallback-page-template';
import { ShopPageTemplate } from '../../../components/templates/shop-page-template';
import { ShoppingCart, useApplicationState } from '../../../contexts/application-state-context';
import { useLanguageContext } from '../../../contexts/language-context';
import {
  OrderObjectType,
  OrganisationObjectType,
  ReservationObjectType,
  ReservationStatusEnumType,
  ShopObjectType,
} from '../../../generated/graphql';
import { MinimalServerError } from '../../../types';
import { getOrganisationAndShop } from '../../../utils/api';
import { formatCurrency } from '../../../utils/currency';
import { ImageFitMode } from '../../../utils/imgix';
import { nullthrows } from '../../../utils/invariant';
import { formatAmount } from '../../../utils/number';
import { formatPhonenumber } from '../../../utils/phonenumber';
import {
  getLocalisedString,
  getReservationPrices,
  getReservationProductImages,
  getReservationProductsName,
} from '../../../utils/product';
import { Sentry, captureException } from '../../_app';

const CAROUSEL_TIMEOUT = 5000;

export interface ProductCardProps {
  reservation: ReservationObjectType;
  organisation: OrganisationObjectType;
}

function ProductCard(props: ProductCardProps) {
  let { reservation, organisation } = props;
  let router = useRouter();
  let { addToCart, shopId } = useApplicationState();
  let [shownImage, setShownImage] = React.useState(0);
  let [images, setImages] = React.useState<string[]>([]);
  let [carouselTimeout, setCarouselTimeout] = React.useState<number | null>(null);
  let { language } = useLanguageContext();
  let { translate } = useTranslator();

  const reservationPrices = React.useMemo(() => {
    return getReservationPrices(reservation);
  }, [reservation]);

  let name = React.useMemo(() => {
    return getReservationProductsName(reservation, language) || 'Removed product';
  }, [reservation.vendingProducts]);

  let discountPercentage = React.useMemo(() => {
    return reservation?.vendingDiscount ? formatAmount(reservation.vendingDiscount / 100) : '';
  }, [reservation]);

  React.useEffect(() => {
    setImages(getReservationProductImages(reservation));
    setShownImage(0);
  }, [reservation.vendingProducts]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      if (carouselTimeout) {
        clearTimeout(carouselTimeout);
      }

      let timeout = setTimeout(() => {
        if (shownImage < images.length - 1) {
          setShownImage(shownImage + 1);
        } else {
          setShownImage(0);
        }
      }, CAROUSEL_TIMEOUT);

      setCarouselTimeout(timeout as unknown as number);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [images, shownImage, typeof window]);

  const isSold = reservation.status !== ReservationStatusEnumType.Reserved;
  const vendingDiscount = reservation.vendingDiscount ?? 0;
  return (
    <div
      className="relative w-full bg-white border grid grid-col-1"
      style={{
        gridTemplateRows: 'auto 1fr auto',
      }}
    >
      <div className="w-full h-auto relative">
        <NextLink
          href="/[organisation]/[shopId]/product/[reservationId]"
          as={`/${organisation.pathname}/${shopId}/product/${reservation._id}`}
        >
          {images.length > shownImage && (
            <Image
              url={images[shownImage]}
              width={500}
              height={500}
              fitMode={ImageFitMode.Crop}
              sizes="(max-width: 500px) 400px, 800px"
              alt={name}
              className="w-full h-auto overflow-hidden"
            />
          )}
        </NextLink>
        {(!!discountPercentage || isSold) && (
          <div className="absolute m-2 text-lg font-bold py-2 px-4 bg-red-600 text-white" style={{ top: 5, right: 5 }}>
            {isSold ? translate('product.sold') : `-${discountPercentage}%`}
          </div>
        )}
      </div>

      <div className="pt-6 pb-2 px-8 text-center text-xl uppercase font-bold text-gray-900 leading-6">
        <NextLink
          href="/[organisation]/[shopId]/product/[reservationId]"
          as={`/${organisation.pathname}/${shopId}/product/${reservation._id}`}
        >
          {name}
        </NextLink>
      </div>

      <div className="text-center w-full py-4">
        <div className="mb-2">
          {vendingDiscount > 0 && (
            <div className="uppercase text-gray-700 line-through">{formatCurrency(reservationPrices.totalPrice)}</div>
          )}
          <div className="text-xl uppercase text-gray-800 font-bold">
            {formatCurrency(reservationPrices.totalPriceWithDiscount)}
          </div>
        </div>
        <div className="w-full flex justify-center py-2">
          <div>
            <NextLink
              href="/[organisation]/[shopId]/product/[reservationId]"
              as={`/${organisation.pathname}/${shopId}/product/${reservation._id}`}
              passHref
            >
              <Button size="sm">{translate('product.infoButtonText')}</Button>
            </NextLink>
          </div>
          <div className="ml-2">
            <Button
              size="sm"
              appearance="primary"
              disabled={isSold}
              onPress={() => {
                if (isSold) return;

                addToCart([reservation]);
                router.push(
                  '/[organisation]/[shopId]/order/shopping-cart',
                  `/${organisation.pathname}/${shopId}/order/shopping-cart`
                );
              }}
            >
              {translate(isSold ? 'product.sold' : 'product.orderButtonText')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PageProps {
  organisation?: OrganisationObjectType;
  shop: ShopObjectType;
  shoppingCart: ShoppingCart;
  order?: OrderObjectType;
  reservations: Array<ReservationObjectType>;
  error?: MinimalServerError;
}

function PageContent(props: PageProps) {
  const { shop, organisation } = props;
  const { reservations } = useApplicationState();
  const { translate, language } = useTranslator();
  const [searchValue, setSearchValue] = useState('');

  const sortedReservations = useMemo(() => {
    if (!searchValue || !reservations.length) {
      return reservations;
    }

    const availableReservations = reservations.filter((r) => r.status === ReservationStatusEnumType.Reserved);

    // Search in available reservations...
    const mappedValues = availableReservations.map((r) => {
      const productNames = r.vendingProducts
        .map((p) => (p.product ? getLocalisedString(p.product.name, language) : ''))
        .filter(Boolean);
      return {
        productNames,
      };
    });
    const fuse = new Fuse(mappedValues, {
      isCaseSensitive: false,
      keys: ['productNames'],
    });
    return fuse.search(searchValue).map((v) => availableReservations[v.refIndex]);
  }, [reservations, searchValue, language]);

  return (
    <>
      <div className="bg-white">
        <div className="max-w-screen-xl mx-auto p-8">
          <div className="md:flex flex-row-reverse justify-between">
            <div>
              {organisation?.logoUrl && (
                <div className="h-36 w-36 overflow-hidden mb-6 md:mb-0">
                  <Image url={organisation.logoUrl} alt={shop.name} width={150} height={150} sizes="150px" />
                </div>
              )}
            </div>
            <div>
              <div>
                <Heading>{shop.name}</Heading>
                <div>
                  {shop.address.street} {shop.address.streetNumber} • {shop.address.zipcode} {shop.address.city}
                </div>
                <div>TEL {formatPhonenumber(shop.phonenumber)}</div>
              </div>
              <div className="text-gray-800 font-medium rounded my-4">
                {translate('overview.pickupOrderWithin', {
                  vendingPickupTime: shop.vendingPickupTime.toString(10),
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <OrderHint />

      <div className="max-w-screen-xl mx-auto px-8 pt-8 pb-16">
        <Heading>{translate('products.currentlyAvailableTitle')}</Heading>
        {sortedReservations.length > 0 ? (
          <div>
            <div className="mb-4">
              <TextField
                label={translate('products.search')}
                onChange={(val) => setSearchValue(val)}
                value={searchValue}
              />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedReservations.map((reservation) => {
                return (
                  <ProductCard
                    reservation={reservation}
                    organisation={nullthrows(organisation, 'Organisation not found')}
                    key={reservation._id}
                  />
                );
              })}
            </div>

            {!!shop.webshopUrl && (
              <div className="flex flex-col items-center my-8">
                <div className="text-md mb-2">{translate('productsOverview.webshopFallbackTitle')}</div>
                <LinkButton href={shop.webshopUrl}>{translate('productsOverview.webshopLinkButtonText')}</LinkButton>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex justify-center font-medium text-xl text-gray-700 my-32 text-center">
              {translate('products.noProductsAvailable')}
            </div>
            {!!shop.webshopUrl && (
              <div className="flex flex-col items-center my-8">
                <LinkButton href={shop.webshopUrl}>{translate('productsOverview.webshopLinkButtonText')}</LinkButton>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default function Index(props: PageProps) {
  let { organisation, shop, shoppingCart, order, reservations, error } = props;

  if (error) {
    return (
      <FallbackPageTemplate>
        <ErrorFallback error={error} />
      </FallbackPageTemplate>
    );
  }

  if (!organisation) {
    return (
      <FallbackPageTemplate>
        <NotFound />
      </FallbackPageTemplate>
    );
  }

  return (
    <ShopPageTemplate
      organisation={organisation}
      shop={shop}
      shoppingCart={shoppingCart}
      order={order}
      reservations={reservations}
    >
      <AppBanner url={shop._id ? `woyti://shop/${shop._id}` : 'woyti://'} />

      <PageContent {...props} />
    </ShopPageTemplate>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  try {
    let { shopId } = ctx.query;
    let organisationData = await getOrganisationAndShop(ctx, shopId as string);

    if (!organisationData) {
      return {
        props: {},
      };
    } else {
      try {
        return {
          props: {
            organisation: organisationData.organisation,
            shop: organisationData.shop,
            reservations: organisationData.reservations,
            shoppingCart: organisationData.shoppingCart,
            order: organisationData.order,
          },
        };
      } catch (err) {
        await captureException(err);
        return {
          props: {},
        };
      }
    }
  } catch (err) {
    const eventId = captureException(err);
    await Sentry.flush();

    return {
      props: {
        error: {
          message: err.message,
          eventId,
        },
      },
    };
  }
}
